import { SubscriptionStatus } from "../types";
import { SUPPORT_EMAIL } from "../constants";
// Shared with useTenantAdmin so the gate and its explanation cannot disagree — see tenantAccess.ts.
import { isAdminRole, isAdminTenantType, isProviderRole } from "./tenantAccess";
import { renewsByCheckout } from "./billingAccess";
import { checkoutPath } from "./signupIntent";

// ENG-2612: why the org-admin gate closed, in words.
//
// `deriveTenantAdmin` ANDs three axes — role, tenant type, active licence — and when any one fails
// the admin pages simply aren't in the nav, with a deep link falling back to Home. Nothing said why,
// so a smaller app was indistinguishable from a bug, a permissions change, or nothing at all. Four
// admins of our own org lost the console for six weeks and nobody noticed.
//
// Same failure as the silent empty states in ENG-2591/2595, one layer up: the app knows exactly why
// it degraded and doesn't say. Pure, so the branches are assertable without a render.

export type AdminGateReason =
  /** Holds an admin role in a qualifying tenant, but the tenant's subscription isn't active. */
  | "subscription_inactive"
  /** Holds an admin role, but this tenant type has no admin surface (e.g. a personal tenant). */
  | "tenant_type_unsupported";

export interface AdminGateExplanation {
  reason: AdminGateReason;
  /** One line, naming the failing axis rather than the symptom. */
  title: string;
  detail: string;
  /**
   * What this person can actually do about it.
   *
   * ENG-3525: `billing` for an owner AND an admin, as the backend's `mayManageBilling` allows, except
   * in a family, whose billing is the owner's alone (ENG-3246). A provider's operator gets `none`:
   * billing is never theirs.
   */
  action: "billing" | "checkout" | "none";
  /** For `checkout`: where this organization pays (ENG-3181). */
  checkoutPath?: string;
  /** The checkout button's words: "Renew Haven Family" (or Business) for a plan that ended, "Choose a plan" before one. */
  checkoutLabel?: string;
}

/**
 * ENG-3809's title for a family whose plan ended, said the same to its owner, its admins and its
 * members (consistency audit, 2026-10-15). With no name it never starts "your family's…" in lowercase.
 */
export const familyPlanEndedTitle = (familyName: string | null | undefined): string => {
  const name = familyName?.trim();
  return name ? `Haven Family for ${name} has ended` : "Your family's Haven Family plan has ended";
};

/**
 * A lapsed family, said to its owner or an admin (consistency audit, 2026-10-15): the plan-ended title
 * all three roles read, and only the owner renews (ENG-3246). [viaCheckout] is `renewsByCheckout`;
 * otherwise the owner renews in the billing portal (a Stripe `unpaid`, or a family that predates
 * self-serve checkout), and is sent there in a family's words rather than the organization console's.
 */
function familyLapse(name: string, role: string, ended: boolean, viaCheckout: boolean): AdminGateExplanation {
  const title = ended ? familyPlanEndedTitle(name) : `${name.trim() || "Your family"} isn't paid for`;
  if (role !== "owner") {
    return { reason: "subscription_inactive", title, detail: ended ? "Only the owner can renew it." : "Only the owner can choose a plan.", action: "none" };
  }
  if (viaCheckout) {
    return {
      reason: "subscription_inactive",
      title,
      // ENG-3897: as the portal path below and the plan-ended email say, protection never stopped.
      detail: ended
        ? "Renew it to turn Haven Family back on for everyone. Haven keeps protecting your family meanwhile."
        : "Choose monthly or yearly to turn Haven Family on.",
      action: "checkout",
      checkoutPath: checkoutPath("family"),
      checkoutLabel: ended ? "Renew Haven Family" : "Choose a plan",
    };
  }
  return {
    reason: "subscription_inactive",
    title,
    detail: ended
      ? "Update the payment in Billing to turn Haven Family back on for everyone. Haven keeps protecting your family meanwhile."
      : "Haven Family turns on for everyone once it's paid for. Haven keeps protecting your family meanwhile.",
    action: "billing",
  };
}

/** ENG-3525: what a lapsed organization's owner or admin is told. Both can renew it under Billing. */
const LAPSED_ADMIN_DETAIL =
  "Admin tools are unavailable until it's renewed. Your members keep their protection — " +
  "only the organization console is affected.";

const MEMBERS_DEACTIVATED_DETAIL =
  "Admin tools are unavailable until it's renewed, and everyone except the owner has lost the " +
  `organization's protection. Email ${SUPPORT_EMAIL} to renew it.`;


/**
 * Explain a closed gate — or return null when there is nothing to explain.
 *
 * Null is the common, correct case: a member, a solo user, or a signed-out account was never going
 * to have admin tools, and telling them what they're missing would be noise. This only speaks up
 * when someone holds an admin role and something ELSE took the console away, which is precisely the
 * situation that reads as a bug.
 */
export function describeAdminGate(
  status: SubscriptionStatus | null | undefined,
): AdminGateExplanation | null {
  // ENG-2661: checked FIRST, because in exactly this case the fields below describe the wrong
  // tenant. A lapsed licence makes /subscription-status resolve past the org to the caller's
  // PERSONAL tenant, which reports no role and a synthetic status of "active" — so the checks below
  // returned null on their first line and this notice never appeared for the one situation it was
  // built for. The server sends lockedOutOrg only in that state, so its presence is the whole test.
  //
  // The role is re-checked here even though the server already filters to admin roles. This is not
  // redundancy for its own sake: the server's first cut of this field used
  // listDefaultTenantCandidates, which filters on tenant TYPE and not role, so it briefly offered a
  // lockout notice to plain members about tools they never had. Silence for members is the design
  // (see the note above), and it should not depend on one filter in one route staying right.
  const locked = status?.lockedOutOrg;
  if (locked && isAdminRole(locked.role)) {
    if (locked.tenantType === "family") {
      return familyLapse(locked.name, locked.role, locked.subscriptionStatus != null, renewsByCheckout(status));
    }
    // ENG-3181: a business that never paid, or whose subscription was cancelled, pays at its own checkout.
    if (renewsByCheckout(status)) {
      const ended = locked.subscriptionStatus != null;
      const name = locked.name.trim();
      return {
        reason: "subscription_inactive",
        title: ended ? `Haven Business for ${name || "your organization"} has ended` : `${name || "Your organization"} isn't paid for`,
        detail: ended ? "Renew it to turn Haven Business back on for everyone." : "Choose monthly or yearly to turn Haven Business on.",
        action: "checkout",
        checkoutPath: checkoutPath(locked.tenantType),
        checkoutLabel: ended ? "Renew Haven Business" : "Choose a plan",
      };
    }
    return {
      reason: "subscription_inactive",
      title: `${locked.name.trim() || "your organization"}'s subscription is inactive`,
      detail: locked.membersDeactivated ? MEMBERS_DEACTIVATED_DETAIL : LAPSED_ADMIN_DETAIL,
      action: "billing",
    };
  }

  const role = status?.role ?? null;
  const tenantType = status?.tenantType ?? null;
  const org = status?.tenantName?.trim() || "your organization";

  // ENG-3407: a provider's operator reaches the console by the same gate as an admin (tenantAccess
  // `isConsoleRole`), so a closed gate is explained to them too — the branches below would otherwise
  // leave them in a smaller app with nothing said, which is the ENG-2612 failure. Billing is never
  // theirs (the backend's `mayManageBilling`), so the only way forward is the organization's own.
  if (isProviderRole(role)) {
    if (!isAdminTenantType(tenantType)) {
      return {
        reason: "tenant_type_unsupported",
        title: "This organization has no admin tools",
        detail: `Your IT provider's access reaches ${org}, but this type of account doesn't include the ` +
          `organization console. Email ${SUPPORT_EMAIL} if you expected it to.`,
        action: "none",
      };
    }
    if (!(!!status?.hasSubscription && status?.status === "active")) {
      return {
        reason: "subscription_inactive",
        title: `${org}'s subscription is inactive`,
        detail: "The organization console is unavailable until it's renewed, and only an owner or " +
          "admin of the organization can renew it.",
        action: "none",
      };
    }
    return null;
  }

  if (!isAdminRole(role)) return null;

  if (!isAdminTenantType(tenantType)) {
    return {
      reason: "tenant_type_unsupported",
      title: "This account has no admin tools",
      detail:
        `You're an ${role} of ${org}, but this type of account doesn't include the organization ` +
        `console. Email ${SUPPORT_EMAIL} if you expected it to.`,
      action: "none",
    };
  }

  const hasActiveLicense = !!status?.hasSubscription && status?.status === "active";
  if (!hasActiveLicense && tenantType === "family" && role) {
    return familyLapse(status?.tenantName ?? "", role, status?.status != null, renewsByCheckout(status));
  }
  if (!hasActiveLicense) {
    return {
      reason: "subscription_inactive",
      // Names the axis, not the symptom: "you don't have access" would send an admin looking at
      // their role, which is fine — it's the subscription that lapsed.
      title: `${org}'s subscription is inactive`,
      detail: LAPSED_ADMIN_DETAIL,
      action: "billing",
    };
  }

  return null;
}

/**
 * Whether the person is an admin whose gate is closed. Convenience for call sites that only need the
 * boolean, so they don't reimplement the "has an admin role but isTenantAdmin is false" test.
 */
export function isLockedOutAdmin(status: SubscriptionStatus | null | undefined): boolean {
  return describeAdminGate(status) !== null;
}
